import * as React from 'react'
import styled from 'styled-components'

import * as plume from '@ulule/owl-kit-components/next'
import * as utils from '@owl-nest/utils'

import * as entities from '@boutique/entities'
import * as cold from '@owl-nest/cold-data/client'

import { useGiftIdeaListing } from '../hooks/useGiftIdeaListing'

import * as services from '@boutique/services'

export function Selects(): React.ReactElement {
  const { response, query, apply, setCriterion, setCategory, setPerson, setPriceRange } = useGiftIdeaListing()

  React.useEffect(() => {
    if (
      (query.facetFilters?.person === 'puericulture' || query.facetFilters?.person === 'animaux') &&
      query.facetFilters?.category !== undefined
    ) {
      setCategory(undefined)
    }
  }, [query.facetFilters?.person, query.facetFilters?.category])

  const personState = getFilterState(response.data, query, 'person', PERSON_OPTIONS)

  const categoryState = getFilterState(response.data, query, 'category', CATEGORY_OPTIONS)

  const budgetState = getPriceState(response.data, query, BUDGET_OPTIONS)

  const criteriaState = getFilterState(response.data, query, 'criterion', CRITERIA_OPTIONS)

  const noCategorySelected = query.facetFilters?.category === 'all' || query.facetFilters?.category === undefined

  return (
    <Wrapper>
      <Block>
        <SelectField
          $zindex={4}
          native
          disabled={personState.isDisabled}
          label="Je veux offrir un cadeau pour"
          onChange={(selected) => {
            apply(setPerson(selected[0].value), { scroll: false })
          }}
          placeholder="Choisir une personne"
          selected={[personState.selected?.value]}
        >
          <plume.Option native value={undefined} label={'Choisir une personne'}>
            {'Choisir une personne'}
          </plume.Option>
          {personState.options.map((option) => (
            <plume.Option
              native
              key={option.value}
              value={option.value}
              label={option.label}
              disabled={
                (!noCategorySelected && (option.value === 'puericulture' || option.value === 'animaux')) ||
                option.isDisabled
              }
            >
              {option.label}
            </plume.Option>
          ))}
        </SelectField>
        {query.facetFilters?.person !== 'puericulture' && query.facetFilters?.person !== 'animaux' && (
          <SelectField
            $zindex={3}
            native
            disabled={categoryState.isDisabled}
            label="qui aime"
            height={250}
            onChange={(selected) => {
              apply(setCategory(selected[0].value), { scroll: false })
            }}
            placeholder="Choisir une catégorie"
            selected={[categoryState.selected?.value]}
          >
            <plume.Option native value={undefined} label={'Choisir une catégorie'}>
              {'Choisir une catégorie'}
            </plume.Option>
            {categoryState.options.map((option) => (
              <plume.Option
                native
                disabled={option.isDisabled}
                key={option.value}
                value={option.value}
                label={option.label}
              >
                {option.label}
              </plume.Option>
            ))}
          </SelectField>
        )}
      </Block>

      <Block>
        <SelectField
          $zindex={2}
          native
          disabled={budgetState.isDisabled}
          label="J’ai un budget de"
          onChange={(selected) => {
            const option = budgetState.options.find((option) => option.id === selected[0].value)
            apply(setPriceRange(option?.value), { scroll: false })
          }}
          placeholder="Choisir un budget"
          selected={[budgetState.selected?.id]}
        >
          <plume.Option native value={undefined} label={'Choisir un budget'}>
            {'Choisir un budget'}
          </plume.Option>
          {budgetState.options.map((option) => (
            <plume.Option native disabled={option.isDisabled} key={option.id} value={option.id} label={option.label}>
              {option.label}
            </plume.Option>
          ))}
        </SelectField>
      </Block>

      <Block lastChild={true}>
        <SelectField
          $zindex={1}
          native
          disabled={criteriaState.isDisabled}
          label="J’aimerais qu’il soit"
          placeholder="Choisir un critère (optionnel)"
          height={250}
          onChange={(selected) => {
            apply(setCriterion(selected[0].value), { scroll: false })
          }}
          selected={[criteriaState.selected?.value]}
        >
          <plume.Option native value={undefined} label={'Choisir un critère (optionnel)'}>
            {'Choisir un critère (optionnel)'}
          </plume.Option>
          {criteriaState.options.map((option) => (
            <plume.Option
              native
              key={option.value}
              value={option.value}
              label={option.label}
              disabled={option.isDisabled}
            >
              {option.label}
            </plume.Option>
          ))}
        </SelectField>
      </Block>
    </Wrapper>
  )
}

function getPriceState(
  data: entities.search.giftIdea.Result | undefined,
  query: entities.search.giftIdea.Query,
  options: { label: string; value: { start?: number; end?: number } }[],
) {
  const facet = data?.facets.price

  const optionList = options.map((option) => {
    const total = Object.entries(facet?.data ?? {}).reduce((total, [key, value]) => {
      const price = Number(key)
      if (
        (option.value.start === undefined || price >= option.value.start) &&
        (option.value.end === undefined || price <= option.value.end)
      ) {
        return total + value
      }
      return total
    }, 0)

    const isSelected = query.priceRange?.end === option.value.end && query.priceRange?.start === option.value.start
    const isDisabled = facet !== undefined && total === 0

    return {
      ...option,
      id: `${option.value.start}:${option.value.end}`,
      isSelected,
      isDisabled,
    }
  })

  return {
    options: optionList,
    selected: optionList.find((option) => option.isSelected),
    isDisabled:
      (!optionList.some((option) => option.isSelected) && facet?.total === 0) ||
      optionList.every((option) => option.isDisabled),
  }
}

function getFilterState(
  data: entities.search.giftIdea.Result | undefined,
  query: entities.search.giftIdea.Query,
  filter: keyof entities.search.giftIdea.Filters,
  options: { label: string; value: string }[],
) {
  const facet = data?.facets[filter]

  const optionList = options.map((option) => {
    const isSelected = query.facetFilters?.[filter]?.includes(option.value)
    let count = 0
    if (filter === 'category' && option.value in services.search.giftIdea.FAKE_CATEGORIES) {
      const tags: string[] = (services.search.giftIdea.FAKE_CATEGORIES as any)[option.value]
      count = tags.reduce((count, slug) => {
        return count + (facet?.data[utils.text.slugify(slug)] ?? 0)
      }, 0)
    } else {
      count = facet?.data[utils.text.slugify(option.value)] ?? 0
    }

    return {
      ...option,
      isSelected,
      isDisabled: option.value === 'all' ? false : !isSelected && count === 0,
    }
  })

  return {
    options: optionList,
    selected: optionList.find((option) => option.isSelected),
    isDisabled: !optionList.some((option) => option.isSelected) && facet?.total === 0,
  }
}

const PERSON_OPTIONS = [
  { label: 'Un enfant', value: 'enfant' },
  { label: 'Une femme', value: 'femme' },
  { label: 'Un homme', value: 'homme' },
  { label: 'Un bébé', value: 'puericulture' },
  { label: 'Un adulte', value: 'all' },
  { label: 'Un animal de compagnie', value: 'animaux' },
]

const CATEGORY_OPTIONS = [
  ...Object.values(cold.types['category'])
    .filter((slug) => !['culture-et-loisirs'].includes(slug))
    .map((slug) => {
      const category = cold.tags[cold.taxonomies[slug].children.tags[0]]
      return { value: category.slug, label: category.category!.altName }
    }),
  { label: 'Exercer sa main verte', value: 'jardin' },
  { label: 'Le jeux en tous genres', value: 'jeux-jouets-et-loisirs' },
  { label: 'Bouquiner tranquillement', value: 'livres-et-papeterie' },
  { label: 'Se dépenser en faisant du sport', value: 'sport-et-plein-air' },
  { label: 'Peu importe', value: 'all' },
].sort((a, b) => {
  if (a.value === 'all') {
    return 1
  }
  if (b.value === 'all') {
    return -1
  }
  return a.label.localeCompare(b.label)
})

const BUDGET_OPTIONS = [
  { label: 'Moins de 25 €', value: { end: 25 } },
  { label: 'De 25 € à 50 €', value: { start: 25, end: 50 } },
  { label: 'De 50 € à 80 €', value: { start: 50, end: 80 } },
  { label: 'Plus de 80 €', value: { start: 80 } },
  { label: 'Peu importe', value: { start: 0, end: Infinity } },
]

const CRITERIA_OPTIONS = [
  ...cold.taxonomies[cold.types['social-pillar']].children.tags.map((tagSlug) => {
    const tag = cold.tags[tagSlug]
    return {
      label: tag.pillar!.altName,
      value: tag.slug,
    }
  }),
  ...cold.taxonomies[cold.types['environmental-pillar']].children.tags.map((tagSlug) => {
    const tag = cold.tags[tagSlug]
    return {
      label: tag.pillar!.altName,
      value: tag.slug,
    }
  }),
].sort((a, b) => {
  return a.label.localeCompare(b.label)
})

function SelectFieldComponent({ children, label, ...selectProps }: plume.SelectFieldProps) {
  return (
    <plume.SelectField
      label={<plume.styles.heading.XXXS asDesktop="L">{label}</plume.styles.heading.XXXS>}
      {...selectProps}
    >
      {children}
    </plume.SelectField>
  )
}

const SelectField = styled(SelectFieldComponent)<{ $zindex: number }>`
  text-align: left;
  margin: 0 12px;

  ${plume.styles.select.Drawer} {
    z-index: ${({ $zindex }) => $zindex};
    position: relative;
  }

  ${plume.styles.heading.S} {
    letter-spacing: -0.1px;
  }

  ${plume.styles.select.Label} {
    font-weight: 500;
  }
`

const Block = styled.div<{ lastChild?: boolean }>`
  width: 100%;

  ${plume.styles.field.Label} {
    position: relative;
    display: inline-flex;
    width: 100%;
    margin: ${({ lastChild }) => (lastChild ? '0px' : '0 0 32px 0}')};
  }

  ${plume.styles.field.Title} {
    margin: 0;
  }

  ${plume.styles.field.TitleWrapper} {
    align-items: center;
    margin-bottom: 16px;
  }

  ${plume.styles.field.FieldHead} {
    align-items: center;
    flex-direction: row;
    justify-content: center;
  }

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    ${plume.styles.field.Label} {
      flex-direction: row;
      margin-bottom: 0;
      margin: 12px;
      width: auto;
    }

    ${plume.styles.field.FieldHead} {
      justify-content: space-between;
    }

    ${plume.styles.field.Title} {
      margin-right: 16px;
    }

    ${plume.styles.field.TitleWrapper} {
      align-items: baseline;
      margin-bottom: 0;
    }
  }
`

const Wrapper = styled.section`
  position: relative;
  z-index: 1;
`
